import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePlaceBet, usePlaceMulti } from '@/hooks/useBets';
import { useMarkets, MARKETS_QUERY_KEY } from '@/hooks/useMarkets';
import { useMe } from '@/hooks/useMe';
import { ApiClientError } from '@/api/client';
import { formatCents, parseDollarsToCents } from '@/utils/money';
import { BetSlipContext, type BetSlipValue } from './betSlipContext';
import type { Market, Selection } from '@/types/api';
import { computeStakeCap, describeCapReason } from './stakeCap';
import {
  BOARD_REFUSAL_CODES,
  QUOTE_REFUSAL_CODES,
  quoteFromChangedDetails,
  quoteMoved,
  resolveHeldSelection,
  type PriceChange,
} from './quoteSync';
import {
  anyDelayed,
  combinedOdds as productOfOdds,
  formatOdds,
  pickLeg,
  previewPayoutCents,
  removeLeg as dropLeg,
  type Leg,
} from './multi';

export function BetSlipProvider({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  const { data: markets, dataUpdatedAt: boardUpdatedAt } = useMarkets();
  const qc = useQueryClient();
  const placeBet = usePlaceBet();
  const placeMulti = usePlaceMulti();

  const [legs, setLegs] = useState<Leg[]>([]);
  const [dollarStake, setDollarStake] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [priceChange, setPriceChange] = useState<PriceChange | null>(null);
  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const first = legs[0] ?? null;
  const market = first?.market ?? null;
  const selection = first?.selection ?? null;
  const isMulti = legs.length >= 2;
  const combinedOdds = productOfOdds(legs);

  const playBalance = me?.balances.balanceCents ?? 0;
  const feeBps = me?.params?.transactionFeeBps ?? 400;

  const stakeCents = parseDollarsToCents(dollarStake || '0');
  /*
    The fee is still COMPUTED here because it is still CHARGED — stake + fee is
    what must fit inside the Play Balance, and the cap preview below needs it.
    It is no longer ITEMISED on the slip (stakeholder feedback 2026-10-13, and
    the standing product rule that the fee is disclosed as a running "Fees paid"
    total on the account, not per bet). Disclosure lives in ActivityCard. A
    multi charges the fee ONCE on the ticket stake, never per leg (0049).
  */
  const feeCents = Math.floor((stakeCents * feeBps) / 10_000);
  const potentialPayout = legs.length ? previewPayoutCents(stakeCents, combinedOdds) : 0;
  const insufficientFunds = stakeCents > 0 && stakeCents + feeCents > playBalance;

  /*
    The cap is previewed from /me so the slip can say "up to $X" before the
    server refuses. The server still decides (0037/0038/0048); this only stops
    a customer composing a stake the server is certain to reject. For a multi
    the C-6 share applies to the ticket stake once and the delayed-price payout
    cap to the PRODUCT (0049), which is what feeding the combined odds and
    "any leg delayed" into the same calculation expresses.
  */
  const cap = legs.length
    ? computeStakeCap({
        playBalanceCents: playBalance,
        feeBps,
        decimalOdds: combinedOdds,
        dataDelayed: anyDelayed(legs),
        params: (me?.params ?? {}) as Parameters<typeof computeStakeCap>[0]['params'],
      })
    : null;
  const maxStakeCents = cap?.maxStakeCents ?? null;
  const capReason = cap ? describeCapReason(cap.reason) : null;
  const overCap = cap !== null && stakeCents > cap.maxStakeCents;

  const isPending = placeBet.isPending || placeMulti.isPending;
  const canSubmit =
    legs.length > 0 &&
    stakeCents > 0 &&
    !insufficientFunds &&
    !overCap &&
    !isPending &&
    !isRefreshingQuote;

  /*
    LIVE PRICE SYNC. Every time the board refreshes (useMarkets polls, and a
    refusal below forces a refetch), every held leg is re-resolved and its
    server token replaced. The customer sees the price tick in the slip rather
    than discovering the movement as a 422.

    Keyed on `dataUpdatedAt`, not on the data object: it must run once per
    FETCH. After a `quote_changed` refusal the slip already holds the server's
    newer token while the cached board still carries the older one; re-running
    on every render would "correct" the slip back to the stale board. A fresh
    fetch has both in agreement again.
  */
  const heldRef = useRef<Leg[]>([]);
  useEffect(() => {
    heldRef.current = legs;
  }, [legs]);

  /** Re-resolve every held leg against a board; returns the fresh legs and the first price that moved. */
  const resync = useCallback((board: Market[] | undefined, held: Leg[]) => {
    let change: PriceChange | null = null;
    let moved = false;
    const next = held.map((leg) => {
      const fresh = resolveHeldSelection(board, leg);
      if (!fresh) return leg; // left the board — the next submit says so, with the server's words
      if (!quoteMoved(leg.selection, fresh.selection)) return leg;
      moved = true;
      if (!change && leg.selection.decimalOdds !== fresh.selection.decimalOdds) {
        change = { fromOdds: leg.selection.decimalOdds, toOdds: fresh.selection.decimalOdds };
      }
      return fresh;
    });
    return { next, moved, change };
  }, []);

  useEffect(() => {
    const held = heldRef.current;
    if (!held.length || !markets) return;
    const { next, moved, change } = resync(markets, held);
    if (!moved) return;
    if (change) setPriceChange(change);
    setLegs(next);
    setErrorMsg('');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per fetch, by design (see above)
  }, [boardUpdatedAt]);

  const pick = useCallback((m: Market, s: Selection) => {
    setLegs((prev) => {
      const r = pickLeg(prev, m, s);
      if (r.outcome === 'full') {
        setErrorMsg('A multi can have up to 10 legs.');
        return prev;
      }
      setErrorMsg('');
      return r.legs;
    });
    setSuccessMsg('');
    setPriceChange(null);
    setSheetOpen(true);
  }, []);

  const isPicked = useCallback(
    (selectionId: string) => legs.some((l) => l.selection.id === selectionId),
    [legs],
  );

  const removeLeg = useCallback((selectionId: string) => {
    setLegs((prev) => {
      const next = dropLeg(prev, selectionId);
      if (!next.length) setSheetOpen(false);
      return next;
    });
    setErrorMsg('');
  }, []);

  const clear = useCallback(() => {
    setLegs([]);
    setDollarStake('');
    setErrorMsg('');
    setPriceChange(null);
    setSheetOpen(false);
  }, []);

  const setStake = useCallback((value: string) => {
    setDollarStake(value);
    setErrorMsg('');
  }, []);

  const openSheet = useCallback(() => setSheetOpen(true), []);
  const closeSheet = useCallback(() => setSheetOpen(false), []);
  const dismissSuccess = useCallback(() => setSuccessMsg(''), []);

  /*
    A quote refusal is handled, not just displayed.
      quote_changed  → the server sent the CURRENT terms; apply them to the
                       leg it names (details.selectionId on a multi, the only
                       leg on a single) and ask for one more tap. The customer
                       never leaves the slip.
      expired/stale  → no replacement terms on the wire; refetch the board and
                       re-resolve every leg. If the runners are still there the
                       slip is re-armed with the live prices; if not, say so.
      board refusals → the whole catalogue is unplaceable right now (freshness
                       or posture); refetch and tell the customer to retry in a
                       moment — nothing on their side is wrong.
      same fixture   → cannot happen from this slip (pickLeg replaces), but the
                       server is the authority; show its words.
    Anything else is shown in the server's words, unchanged.
  */
  const handleRefusal = useCallback(
    async (err: unknown, held: Leg[]) => {
      if (!(err instanceof ApiClientError)) {
        setErrorMsg((err as Error)?.message || 'Bet placement failed — please try again.');
        return;
      }
      const { code, message, details } = err.error;
      const d = (details ?? {}) as Record<string, unknown>;

      if (code === 'quote_changed') {
        const targetId = typeof d.selectionId === 'string' ? d.selectionId : held[0]?.selection.id;
        const idx = held.findIndex((l) => l.selection.id === targetId);
        const leg = idx >= 0 ? held[idx] : undefined;
        const fresh = leg ? quoteFromChangedDetails(leg.selection, details) : null;
        if (leg && fresh) {
          setPriceChange({ fromOdds: leg.selection.decimalOdds, toOdds: fresh.decimalOdds });
          setLegs((prev) => prev.map((l) => (l.selection.id === leg.selection.id ? { ...l, selection: fresh } : l)));
          setErrorMsg('');
          void qc.invalidateQueries({ queryKey: MARKETS_QUERY_KEY });
          return;
        }
      }

      if (QUOTE_REFUSAL_CODES.has(code) || BOARD_REFUSAL_CODES.has(code)) {
        setIsRefreshingQuote(true);
        try {
          await qc.invalidateQueries({ queryKey: MARKETS_QUERY_KEY });
          const board = qc.getQueryData<Market[]>(MARKETS_QUERY_KEY);
          if (BOARD_REFUSAL_CODES.has(code)) {
            setErrorMsg('Prices are being refreshed right now — give it a moment and try again.');
          } else {
            const gone = held.filter((l) => !resolveHeldSelection(board, l));
            if (gone.length) {
              setErrorMsg(
                gone.length === held.length
                  ? 'That selection is no longer available to bet on. Pick another from the board.'
                  : `${gone.map((l) => l.selection.label).join(', ')} is no longer available — remove it and place the rest.`,
              );
            } else {
              const { next, moved, change } = resync(board, held);
              if (change) setPriceChange(change);
              if (moved) setLegs(next);
              setErrorMsg(moved ? 'Price refreshed — check it and tap Place bet again.' : message);
            }
          }
        } finally {
          setIsRefreshingQuote(false);
        }
        return;
      }

      setErrorMsg(message || 'Bet placement failed — please try again.');
    },
    [qc, resync],
  );

  const submit = useCallback(() => {
    if (!legs.length) return;
    const held = legs;
    setSuccessMsg('');
    setErrorMsg('');
    const onSuccess = (placedStake: number, label: string, odds: string) => {
      setSuccessMsg(`Bet placed — ${formatCents(placedStake)} on ${label} @ ${odds}.`);
      setDollarStake('');
      setLegs([]);
      setPriceChange(null);
      setSheetOpen(false);
    };
    const onError = (err: unknown) => {
      void handleRefusal(err, held);
    };

    if (held.length === 1) {
      const { market: m, selection: s } = held[0]!;
      placeBet.mutate(
        {
          marketId: m.id,
          selectionId: s.id,
          /* quoteId is the server's own price token. The server owns the price
             (migration 0036): decimalOdds below is a CLAIM it checks against the
             stored quote, never the price struck. Omitting the token is a 422
             quote_missing, not a fallback. */
          quoteId: s.quoteId,
          decimalOdds: s.decimalOdds,
          stakeCents,
        },
        {
          onSuccess: (bet) => onSuccess(bet.stakeCents, s.label, Number(bet.combinedDecimalOdds).toFixed(2)),
          onError,
        },
      );
      return;
    }

    placeMulti.mutate(
      {
        /* Only the price tokens travel. The server prices the ticket from its
           persisted quotes and refuses a repeated fixture (0049). */
        legs: held.map((l) => ({ quoteId: l.selection.quoteId, selectionId: l.selection.id })),
        stakeCents,
        source: 'balance',
      },
      {
        onSuccess: (bet) =>
          onSuccess(bet.stakeCents, `${held.length}-leg multi`, formatOdds(Number(bet.combinedDecimalOdds))),
        onError,
      },
    );
  }, [legs, stakeCents, placeBet, placeMulti, handleRefusal]);

  const value = useMemo<BetSlipValue>(
    () => ({
      legs,
      market,
      selection,
      isMulti,
      combinedOdds,
      dollarStake,
      successMsg,
      errorMsg,
      priceChange,
      isRefreshingQuote,
      stakeCents,
      feeCents,
      feeBps,
      potentialPayout,
      insufficientFunds,
      maxStakeCents,
      capReason,
      overCap,
      canSubmit,
      isPending,
      sheetOpen,
      openSheet,
      closeSheet,
      pick,
      isPicked,
      removeLeg,
      clear,
      setStake,
      submit,
      dismissSuccess,
    }),
    [
      legs,
      market,
      selection,
      isMulti,
      combinedOdds,
      dollarStake,
      successMsg,
      errorMsg,
      priceChange,
      isRefreshingQuote,
      stakeCents,
      feeCents,
      feeBps,
      potentialPayout,
      insufficientFunds,
      maxStakeCents,
      capReason,
      overCap,
      canSubmit,
      isPending,
      sheetOpen,
      openSheet,
      closeSheet,
      pick,
      isPicked,
      removeLeg,
      clear,
      setStake,
      submit,
      dismissSuccess,
    ],
  );

  return <BetSlipContext.Provider value={value}>{children}</BetSlipContext.Provider>;
}
